import { businessQuotesService } from '@/services/quotes/business';
import type {
  BusinessUnderwritingMessage,
  CalculateQuotePayload,
  CalculateQuoteResponse,
} from '@/services/quotes/types';
import { clearClonedQuoteDraftMark } from '@/shared/utils/clone-quote-status.util';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const RNS_ANALYSIS_REQUIRED_MARKER = 'RNS_ANALYSIS_REQUIRED';
const RNS_UNAVAILABLE_MARKER = 'RNS_UNAVAILABLE';

export class RnsAnalysisRequiredError extends Error {
  readonly rnsAnalysisRequired = true as const;

  constructor() {
    super('Cotação enviada para análise da subscrição.');
    this.name = 'RnsAnalysisRequiredError';
  }
}

export function isRnsAnalysisRequiredError(
  error: unknown
): error is RnsAnalysisRequiredError {
  return error instanceof RnsAnalysisRequiredError;
}

export class RnsUnavailableError extends Error {
  readonly rnsUnavailable = true as const;

  constructor() {
    super(
      'O serviço de consulta de sinistros está temporariamente indisponível. Por favor, tente novamente em alguns minutos.'
    );
    this.name = 'RnsUnavailableError';
  }
}

export function isRnsUnavailableError(error: unknown): error is RnsUnavailableError {
  return error instanceof RnsUnavailableError;
}

/**
 * Cotação empresarial bloqueada pelo auto-underwriting. O backend já gravou
 * StatusPortal 17 (Cotação em Análise) e devolveu a lista de mensagens — o
 * caller deve seguir pro step de cálculo, onde as mensagens são exibidas.
 */
export class UnderwritingAnalysisRequiredError extends Error {
  readonly underwritingAnalysisRequired = true as const;
  readonly policyId: string;
  readonly messages: BusinessUnderwritingMessage[];

  constructor(policyId: string, messages: BusinessUnderwritingMessage[]) {
    super('Cotação enviada para análise da subscrição.');
    this.name = 'UnderwritingAnalysisRequiredError';
    this.policyId = policyId;
    this.messages = messages;
  }
}

export function isUnderwritingAnalysisRequiredError(
  error: unknown
): error is UnderwritingAnalysisRequiredError {
  return error instanceof UnderwritingAnalysisRequiredError;
}

export function useCalculateBusinessQuote() {
  const queryClient = useQueryClient();

  return useMutation({
    retry: false,
    mutationFn: async (payload: CalculateQuotePayload) => {
      const response = await businessQuotesService.calculateQuote(payload);

      if (!response.success || !response.data) {
        // Marca de "cotação enviada para análise" é a presença do campo,
        // mesmo que a lista venha vazia (o detalhe vem pelo GET).
        if (Array.isArray(response.underwritingMessages)) {
          throw new UnderwritingAnalysisRequiredError(
            payload.policyId,
            response.underwritingMessages
          );
        }
        const errMsg = response.error || 'Erro ao calcular cotação';
        if (errMsg.includes(RNS_UNAVAILABLE_MARKER)) {
          throw new RnsUnavailableError();
        }
        if (errMsg.includes(RNS_ANALYSIS_REQUIRED_MARKER)) {
          throw new RnsAnalysisRequiredError();
        }
        throw new Error(errMsg);
      }

      return response.data as CalculateQuoteResponse;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['calculateQuote', variables.policyId], data);
      queryClient.invalidateQueries({
        queryKey: ['proposal-details', variables.policyId],
      });
      clearClonedQuoteDraftMark(variables.policyId);
    },
    onError: (error, variables) => {
      if (isUnderwritingAnalysisRequiredError(error)) {
        // Status mudou para QuoteUnderAnalysis no Insuremo; invalida o detalhe
        // pra que o step de cálculo recarregue com o status correto.
        queryClient.invalidateQueries({
          queryKey: ['proposal-details', variables.policyId],
        });
      }
    },
  });
}
